import React, { useCallback, useMemo, useState } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown, Loader2, X } from 'lucide-react';
import { useHistory } from 'react-router-dom';

import { useBulkPackagingPOTableMutations } from 'components/packaging/components/packagingPOV2/hooks/useBulkPackagingPOTableMutations';
import { PackagingPOActionsCell } from 'components/packaging/components/packagingPOV2/PackagingPOActionsCell';
import { PackagingPOSortConfig } from 'components/packaging/components/packagingPOV2/PackagingPOFilterConfig';
import { PackagingPOStatusCell } from 'components/packaging/components/packagingPOV2/PackagingPOStatusCell';
import {
    formatPurchaseOrderDate,
    formatPurchaseOrderDateTime,
} from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import { Button } from 'shadcn/components/ui/Button';
import { Checkbox } from 'shadcn/components/ui/Checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { cn } from 'shadcn/lib/utils';
import { PaginationControls, ResultsCounter } from 'shared';

const FALLBACK_TEXT = '—';

const renderStatusCell = ({ getValue }) => <PackagingPOStatusCell status={getValue()} fallbackText={FALLBACK_TEXT} />;

const renderActionsCell = ({ row, table }) => {
    const { onCancelPurchaseOrder, onDownloadPdf, onDownloadShipList, onDuplicatePurchaseOrder, onViewPurchaseOrder } =
        table.options.meta ?? {};
    const isSendToVendorDisabled = table.options.meta?.isSendToVendorDisabled;
    const onSendToVendor = table.options.meta?.onSendToVendor;
    const sentToVendorDate = row.original?.sentToVendor ?? row.original?.sentToVendorAt ?? row.original?.lastSentAt;
    const lastSentAt = formatPurchaseOrderDateTime(sentToVendorDate, '') || undefined;
    return (
        <PackagingPOActionsCell
            onViewClick={() => onViewPurchaseOrder?.(row.original)}
            onCancel={() => onCancelPurchaseOrder?.(row.original)}
            onDownloadPdf={() => onDownloadPdf?.(row.original)}
            onDownloadShipList={() => onDownloadShipList?.(row.original)}
            onDuplicate={() => onDuplicatePurchaseOrder?.(row.original)}
            onSendToVendor={() => onSendToVendor?.(row.original)}
            isSendToVendorDisabled={isSendToVendorDisabled?.(row.original)}
            lastSentAt={lastSentAt}
        />
    );
};

/**
 * Returns a stable row identifier (purchase order id or TanStack row id).
 */
function getRowId(row) {
    return row.original?.id ?? row.id;
}

/**
 * Memoized row component to prevent unnecessary re-renders.
 * selectedRows drives row visual state (data-state) for selection styling.
 */
const MemoizedPackagingPORow = React.memo(({ row, selectedRows }) => {
    const rowId = getRowId(row);
    const isSelected = selectedRows.includes(rowId);
    return (
        <TableRow data-state={isSelected ? 'selected' : undefined}>
            {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} className="whitespace-normal break-words text-left">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
            ))}
        </TableRow>
    );
});

/**
 * Renders a v2-styled table for Packaging Purchase Orders.
 * Supports cursor-based pagination when pagination props are provided.
 * Bulk actions: send to vendor(s), download pre-populated CSV, download PDFs (same UX as procurement PO V2 table).
 *
 * @param {Object} props - Component props.
 * @param {Array<Object>} [props.data=[]] - Packaging PO rows.
 * @param {boolean} [props.isLoading=false] - Initial loading state.
 * @param {boolean} [props.isFetching=false] - Whether any fetch is in progress.
 * @param {number} [props.totalCount=0] - Total matching records from API (for ResultsCounter and pagination).
 * @param {boolean} [props.hasNextPage=false] - Whether there is a next page.
 * @param {boolean} [props.hasPrevious=false] - Whether there is a previous page.
 * @param {() => void} [props.onNext] - Callback for next page.
 * @param {() => void} [props.onPrevious] - Callback for previous page.
 * @param {number} [props.currentPage] - Current page number.
 * @param {number} [props.totalPages] - Total number of pages.
 * @param {number} [props.pageSize] - Current page size.
 * @param {(size: number) => void} [props.onPageSizeChange] - Callback when page size changes.
 * @param {number[]} [props.pageSizeOptions] - Page size options.
 * @param {({ text: string, level: 'success'|'error'|'warning'|'info' }) => void} [props.onNotify] - Toasts for bulk actions.
 * @param {string} [props.emptyMessage='No packaging purchase orders found.'] - Empty state message.
 * @returns {JSX.Element} Packaging PO table.
 */
export function PackagingPOTable({
    currentPage,
    data = [],
    emptyMessage = 'No packaging purchase orders found.',
    hasNextPage = false,
    hasPrevious = false,
    isFetching = false,
    isLoading = false,
    isSendToVendorDisabled,
    onCancelPurchaseOrder,
    onDownloadPdf,
    onDownloadShipList,
    onDuplicatePurchaseOrder,
    onNext,
    onNotify,
    onPageSizeChange,
    onPrevious,
    onSendToVendor,
    onSortingChange,
    pageSize,
    pageSizeOptions,
    sorting,
    totalCount: totalCountProp,
    totalPages,
}) {
    const [selectedRows, setSelectedRows] = useState([]);
    const tableData = useMemo(() => (Array.isArray(data) ? data : []), [data]);
    const history = useHistory();
    const { bulkDownloadCsvMutation, bulkDownloadPdfsMutation, bulkSendToVendorMutation } =
        useBulkPackagingPOTableMutations({ onNotify });
    const bulkActionLoading =
        bulkDownloadCsvMutation.isPending || bulkDownloadPdfsMutation.isPending || bulkSendToVendorMutation.isPending;

    /**
     * Navigates to the packaging purchase order entity view for the selected row,
     * preserving the current query string so filter state can be restored.
     */
    const handleViewPurchaseOrder = useCallback(
        (purchaseOrder) => {
            const purchaseOrderId = purchaseOrder?.id;
            const currentFilters =
                (typeof window !== 'undefined' && window?.location?.search ? window.location.search : '') ?? '';

            history.push({
                pathname: purchaseOrderId
                    ? `/packaging/viewPurchaseOrder-v2/${encodeURIComponent(purchaseOrderId)}`
                    : '/packaging/viewPurchaseOrder-v2/unknown',
                state: {
                    purchaseOrder: purchaseOrder ?? null,
                    purchaseOrdersFilters: currentFilters,
                },
            });
        },
        [history],
    );

    const renderSelectCell = useCallback(
        ({ row }) => {
            const rowId = getRowId(row);
            return (
                <Checkbox
                    checked={selectedRows.includes(rowId)}
                    onCheckedChange={(checked) => {
                        setSelectedRows((prev) => (checked ? [...prev, rowId] : prev.filter((id) => id !== rowId)));
                    }}
                    aria-label={selectedRows.includes(rowId) ? 'Deselect row' : 'Select row'}
                />
            );
        },
        [selectedRows],
    );

    const visibleRowIds = useMemo(() => tableData.map((r) => r?.id).filter(Boolean), [tableData]);

    const handleBulkSendToVendor = useCallback(() => {
        if (selectedRows.length === 0 || !onNotify) return;
        const selectedPOs = selectedRows.map((id) => tableData.find((row) => row?.id === id)).filter(Boolean);
        const notFoundCount = selectedRows.length - selectedPOs.length;

        if (notFoundCount > 0 && selectedPOs.length > 0) {
            onNotify({
                level: 'warning',
                text: `${notFoundCount} selected PO(s) not found on current page and will be skipped. Please navigate to those pages to send them.`,
            });
        }

        if (selectedPOs.length === 0) {
            onNotify({
                level: 'error',
                text: 'None of the selected POs are on the current page. Please navigate to the page with your selections.',
            });
            return;
        }

        const authorizedPOs = isSendToVendorDisabled
            ? selectedPOs.filter((po) => !isSendToVendorDisabled(po))
            : selectedPOs;
        const unauthorizedCount = selectedPOs.length - authorizedPOs.length;

        if (authorizedPOs.length === 0) {
            onNotify({
                level: 'error',
                text: 'None of the selected POs can be sent. Only department leads can send POs at or above $200,000.',
            });
            return;
        }

        if (unauthorizedCount > 0) {
            onNotify({
                level: 'warning',
                text: `Skipping ${unauthorizedCount} PO(s) that require department lead authorization.`,
            });
        }

        bulkSendToVendorMutation.mutate(
            { purchaseOrderIds: authorizedPOs.map((po) => po.id) },
            {
                onSuccess: (sendResult) => {
                    if (sendResult.succeededPurchaseOrderIds?.length) {
                        setSelectedRows([]);
                    }
                },
            },
        );
    }, [bulkSendToVendorMutation, isSendToVendorDisabled, onNotify, selectedRows, tableData]);

    const handleBulkDownloadCsv = useCallback(() => {
        if (selectedRows.length === 0 || !onNotify) return;
        bulkDownloadCsvMutation.mutate(
            { purchaseOrderIds: selectedRows },
            {
                onSuccess: () => {
                    setSelectedRows([]);
                },
            },
        );
    }, [bulkDownloadCsvMutation, onNotify, selectedRows]);

    const handleBulkDownloadPdfs = useCallback(() => {
        if (selectedRows.length === 0) {
            if (onNotify) {
                onNotify({ level: 'info', text: 'No POs selected for PDF download.' });
            }
            return;
        }
        if (!onNotify) return;
        const pdfItems = selectedRows.map((id) => ({
            id,
            poRow: tableData.find((row) => row?.id === id) ?? null,
        }));
        bulkDownloadPdfsMutation.mutate(
            { pdfItems },
            {
                onSuccess: (pdfResult) => {
                    const successCount = pdfResult.results.filter((r) => r.success).length;
                    if (successCount > 0) {
                        setSelectedRows([]);
                    }
                },
            },
        );
    }, [bulkDownloadPdfsMutation, onNotify, selectedRows, tableData]);

    const renderSelectHeader = useCallback(() => {
        const count = visibleRowIds.length;
        const selectedCount = visibleRowIds.filter((id) => selectedRows.includes(id)).length;
        const allSelected = count > 0 && selectedCount === count;
        const someSelected = selectedCount > 0 && selectedCount < count;
        let checked = false;
        if (allSelected) checked = true;
        else if (someSelected) checked = 'indeterminate';

        const ariaLabel = checked === true ? 'Deselect all on page' : 'Select all on page';

        return (
            <Checkbox
                checked={checked}
                onCheckedChange={(next) => {
                    if (next === true) {
                        setSelectedRows((prev) => [...new Set([...prev, ...visibleRowIds])]);
                    } else {
                        setSelectedRows((prev) => prev.filter((id) => !visibleRowIds.includes(id)));
                    }
                }}
                aria-label={ariaLabel}
            />
        );
    }, [visibleRowIds, selectedRows]);

    const columns = useMemo(() => {
        const isSortable = (key) => Boolean(PackagingPOSortConfig.sortableFields[key]);
        return [
            {
                id: 'select',
                header: renderSelectHeader,
                enableSorting: false,
                cell: renderSelectCell,
            },
            {
                header: 'PO Number',
                accessorKey: 'id',
                enableSorting: isSortable('id'),
                cell: ({ getValue }) => getValue() || FALLBACK_TEXT,
            },
            {
                header: 'Created On',
                accessorKey: 'createdAt',
                enableSorting: isSortable('createdAt'),
                cell: ({ getValue }) => formatPurchaseOrderDate(getValue(), FALLBACK_TEXT),
            },
            {
                header: 'Status',
                accessorKey: 'status',
                enableSorting: isSortable('status'),
                cell: renderStatusCell,
            },
            {
                header: 'Vendor',
                accessorKey: 'vendorName',
                enableSorting: isSortable('vendorName'),
                cell: ({ getValue }) => getValue() || FALLBACK_TEXT,
            },
            {
                header: 'Scheduled Delivery Date',
                accessorKey: 'scheduledDeliveryDate',
                enableSorting: isSortable('scheduledDeliveryDate'),
                cell: ({ getValue }) => formatPurchaseOrderDate(getValue(), FALLBACK_TEXT),
            },
            {
                header: 'FC/Delivery Location',
                id: 'fcDeliveryLocation',
                enableSorting: false,
                cell: ({ row }) => {
                    const facilityName = row.original?.facilityName ?? '';
                    const shipToCrossDockName = row.original?.shipToCrossDockName ?? null;
                    const combined = shipToCrossDockName
                        ? `${facilityName || FALLBACK_TEXT} / ${shipToCrossDockName}`
                        : facilityName || FALLBACK_TEXT;
                    return combined;
                },
            },
            {
                header: 'Reference #',
                accessorKey: 'referenceNumber',
                enableSorting: isSortable('referenceNumber'),
                cell: ({ row }) => row.original?.referenceNumber ?? row.original?.reference ?? FALLBACK_TEXT,
            },
            {
                id: 'actions',
                header: 'Actions',
                enableSorting: false,
                cell: renderActionsCell,
            },
        ];
    }, [renderSelectCell, renderSelectHeader]);

    const table = useReactTable({
        data: tableData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        manualSorting: true,
        state: {
            sorting: sorting ?? [],
        },
        onSortingChange,
        meta: {
            onCancelPurchaseOrder,
            onDownloadPdf,
            onDownloadShipList,
            onDuplicatePurchaseOrder,
            onSendToVendor,
            isSendToVendorDisabled,
            onViewPurchaseOrder: handleViewPurchaseOrder,
        },
    });

    const loadedCount = tableData.length;
    const totalCount = totalCountProp ?? tableData.length;
    const isRefreshing = isFetching;
    const showPagination = tableData.length > 0;
    const selectionCount = selectedRows.length;
    const hasSelection = selectionCount > 0;
    const bulkControlsDisabled = !hasSelection || bulkActionLoading;

    return (
        <div className="flex flex-1 flex-col gap-2">
            {!isLoading && totalCount > 0 && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2">
                    <div className="shrink-0">
                        <ResultsCounter loadedCount={loadedCount} totalCount={totalCount} isRefreshing={isRefreshing} />
                    </div>
                    <div
                        className={cn(
                            'flex min-w-0 flex-1 flex-wrap items-center justify-center gap-x-3 gap-y-2 rounded-md bg-muted/40 px-2 py-1 transition-opacity',
                            !hasSelection && 'opacity-50',
                        )}
                        role="toolbar"
                        aria-label="Bulk actions for packaging purchase orders"
                    >
                        <span className="text-sm font-medium text-foreground">
                            {selectionCount} {selectionCount === 1 ? 'item' : 'items'} selected
                        </span>
                        <div className="flex flex-wrap items-center gap-2">
                            <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                disabled={bulkControlsDisabled}
                                onClick={handleBulkSendToVendor}
                            >
                                {bulkActionLoading ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                                ) : null}
                                Send Emails to Vendors
                            </Button>
                            <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                disabled={bulkControlsDisabled}
                                onClick={handleBulkDownloadCsv}
                            >
                                {bulkActionLoading ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                                ) : null}
                                Download pre-populated csv
                            </Button>
                            <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                disabled={bulkControlsDisabled}
                                onClick={handleBulkDownloadPdfs}
                            >
                                {bulkActionLoading ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                                ) : null}
                                Download PDFs
                            </Button>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                disabled={bulkControlsDisabled}
                                onClick={() => setSelectedRows([])}
                                aria-label="Clear selection"
                            >
                                <X className="mr-1 h-4 w-4" aria-hidden />
                                Clear selection
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex min-h-[320px] flex-1 flex-col">
                {isLoading ? (
                    <div className="flex flex-1 items-center justify-center p-6">
                        <div className="flex flex-col items-center gap-3">
                            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Loading purchase orders…</span>
                        </div>
                    </div>
                ) : (
                    <div className="pb-6">
                        <Table className="w-full table-fixed">
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => {
                                            const canSort = header.column.getCanSort();
                                            const sorted = header.column.getIsSorted();
                                            return (
                                                <TableHead
                                                    key={header.id}
                                                    className={`whitespace-normal break-words${canSort ? ' cursor-pointer select-none' : ''}`}
                                                    onClick={
                                                        canSort ? header.column.getToggleSortingHandler() : undefined
                                                    }
                                                >
                                                    {header.isPlaceholder ? null : (
                                                        <div className="flex items-center gap-1">
                                                            {flexRender(
                                                                header.column.columnDef.header,
                                                                header.getContext(),
                                                            )}
                                                            {canSort &&
                                                                (() => {
                                                                    if (sorted === 'asc')
                                                                        return <ArrowUp className="h-4 w-4 shrink-0" />;
                                                                    if (sorted === 'desc')
                                                                        return (
                                                                            <ArrowDown className="h-4 w-4 shrink-0" />
                                                                        );
                                                                    return (
                                                                        <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                                                                    );
                                                                })()}
                                                        </div>
                                                    )}
                                                </TableHead>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {table.getRowModel().rows.length ? (
                                    table
                                        .getRowModel()
                                        .rows.map((row) => (
                                            <MemoizedPackagingPORow
                                                key={row.id}
                                                row={row}
                                                selectedRows={selectedRows}
                                            />
                                        ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className="h-24 text-center">
                                            {emptyMessage}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>

                        {showPagination && (
                            <PaginationControls
                                hasPrevious={hasPrevious}
                                hasNext={hasNextPage}
                                onPrevious={onPrevious}
                                onNext={onNext}
                                isLoading={isFetching}
                                currentPage={currentPage}
                                totalPages={totalPages}
                                pageSize={pageSize}
                                onPageSizeChange={onPageSizeChange}
                                pageSizeOptions={pageSizeOptions}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

PackagingPOTable.displayName = 'PackagingPOTable';
